import { ReactNode } from 'react';
import { AuthProvider } from './AuthContext';
import { SiteConfigProvider } from './SiteConfigContext';
import { InviteCodeProvider } from './InviteCodeContext';
import { TrackingCodeProvider } from './TrackingCodeContext';
import { ToastProvider } from './ToastContext';
import type { SiteConfig } from '@/services/siteConfigService';

interface GlobalProviderProps {
  children: ReactNode;
  siteConfig?: SiteConfig; // 可选：从 SSR 注入站点配置
  trackingCodeFromUrl?: string | null; // 可选：从 URL 参数获取的追踪码
}

/**
 * 全局 Context Provider
 * 统一管理所有应用级别的 Context
 *
 * Provider 顺序（外到内）：
 * 1. ToastProvider - 全局 Toast 通知
 * 2. TrackingCodeProvider - 追踪码管理
 * 3. SiteConfigProvider - 站点配置
 * 4. AuthProvider - 用户认证
 * 5. InviteCodeProvider - 邀请码管理
 */
export function GlobalProvider({ children, siteConfig, trackingCodeFromUrl }: GlobalProviderProps) {
  return (
    <ToastProvider>
      <TrackingCodeProvider trackingCodeFromUrl={trackingCodeFromUrl}>
        <SiteConfigProvider initialConfig={siteConfig}>
          <AuthProvider>
            <InviteCodeProvider>{children}</InviteCodeProvider>
          </AuthProvider>
        </SiteConfigProvider>
      </TrackingCodeProvider>
    </ToastProvider>
  );
}

// 重新导出所有 Hooks，方便统一导入
export { useAuth } from './AuthContext';
export { useSiteConfig } from './SiteConfigContext';
export { useInviteCode } from './InviteCodeContext';
export { useTrackingCode } from './TrackingCodeContext';
export { useToast } from './ToastContext';
